/**
 * Account Page
 * FT-98: Simplified to show org name, basic account info, and change password
 * FT-99: Added Personal Details section with name edit
 * Removed license/plan features UI (MVP product - one plan, all features on)
 */
import React, { useState } from 'react';
import { useLicense } from '../../../hooks/useLicense';
import { useCustomDialogs } from '../../../hooks/useCustomDialogs';
import { useAuth } from '../auth/AuthGate';
import { usePermissions } from '../../../hooks/usePermissions';
import { PasswordChangeCard } from './PasswordChangeCard';
import AccessLogTab from './AccessLogTab';
import azureApiClient from '../../../lib/azureApiClient';
import {
  CheckCircle,
  Download,
  Upload,
  User,
  Save,
  ShieldCheck
} from 'lucide-react';

export const AccountPage = () => {
  // FT-98: Only need org and error from license hook now
  const { org, loading, error } = useLicense();

  // FT-99: Get current user for name editing
  const { user: currentUser, refreshAuth } = useAuth();

  // FT-104: Check if user is admin (super_admin or fleet) for Admin Access Log visibility
  const { isAdmin } = usePermissions();

  // FT-459: also pull the dialog HOSTS from the SAME useCustomDialogs()
  // instance so the awaited backup-restore confirm renders and fires.
  const { showAlert, showConfirm, ConfirmDialog, AlertDialog } = useCustomDialogs();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  // FT-99: Name edit state
  const [editName, setEditName] = useState(currentUser?.name || '');
  const [savingName, setSavingName] = useState(false);

  // FT-99/FT-132: Handle name save - now persists to Azure API
  const handleSaveName = async () => {
    const trimmedName = editName.trim();
    if (!trimmedName) {
      showAlert('Please enter your name');
      return;
    }

    if (trimmedName === currentUser?.name) {
      // No change
      return;
    }

    setSavingName(true);
    try {
      // FT-132: Call Azure API to persist name change
      const result = await azureApiClient.updateProfile({ name: trimmedName });

      if (!result.success) {
        throw new Error(result.error || 'Failed to update profile');
      }

      // Also update current_user in localStorage for immediate UI update
      const currentUserData = JSON.parse(localStorage.getItem('current_user') || '{}');
      const updatedCurrentUser = { ...currentUserData, name: trimmedName };
      localStorage.setItem('current_user', JSON.stringify(updatedCurrentUser));

      // Also update sms_pro_user if it exists (used by some auth flows)
      try {
        const smsProUser = JSON.parse(localStorage.getItem('sms_pro_user') || '{}');
        if (smsProUser.id) {
          const updatedSmsProUser = { ...smsProUser, name: trimmedName };
          localStorage.setItem('sms_pro_user', JSON.stringify(updatedSmsProUser));
        }
      } catch (e) {
        console.debug('[AccountPage] sms_pro_user update skipped');
      }

      // Also update sr.auth.users if this user exists there (for localStorage auth mode)
      try {
        const usersData = JSON.parse(localStorage.getItem('sr.auth.users') || '[]');
        const updatedUsers = usersData.map(u =>
          u.id === currentUser?.id ? { ...u, name: trimmedName, modifiedAt: Date.now() } : u
        );
        localStorage.setItem('sr.auth.users', JSON.stringify(updatedUsers));
      } catch (e) {
        // Users list may not exist in Azure auth mode, that's fine
        console.debug('[AccountPage] sr.auth.users not found (Azure mode), skipping');
      }

      // Show success and reload to update header
      showAlert('Name updated successfully. Refreshing...');
      setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      showAlert(`Failed to update name: ${err.message}`);
      setSavingName(false);
    }
  };

  // FT-14: Extract module name from localStorage key (simplified version of storage.js extractModule)
  const extractModule = (key) => {
    if (key.startsWith('crew_familiarisations_')) return 'crew_familiarisations';
    if (key.startsWith('crew_')) return 'crew';
    if (key.startsWith('equipment_register_')) return 'equipment_register';
    if (key.startsWith('equipment_selections_')) return 'equipment_selections';
    if (key.startsWith('maintenance_')) return 'maintenance';
    if (key.startsWith('drill_settings_')) return 'drill_settings';
    if (key.startsWith('drill_')) return 'drills';
    if (key.startsWith('defects_')) return 'defects';
    if (key.startsWith('incidents_')) return 'incidents';
    if (key.startsWith('risk_')) return 'risk_assessments';
    if (key.startsWith('voyage_')) return 'voyages';
    if (key.startsWith('daily_checks_')) return 'daily_checks';
    if (key.startsWith('hours_of_rest_')) return 'hours_of_rest';
    if (key.startsWith('vesselSettings_')) return 'vesselSettings';
    if (key.startsWith('operations_')) return 'operations';
    if (key.startsWith('familiarisation_')) return 'familiarisation';
    if (key.startsWith('log_entries_')) return 'log_entries';
    if (key.startsWith('onboard_training_')) return 'onboard_training';
    if (key.startsWith('audits_')) return 'audits';
    const underscoreIndex = key.indexOf('_');
    return underscoreIndex > 0 ? key.substring(0, underscoreIndex) : 'other';
  };

  // FT-14: Extract vessel ID from localStorage key
  const extractVesselId = (key) => {
    const segments = key.split('_');
    for (let i = segments.length - 1; i >= 0; i--) {
      const segment = segments[i];
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
        return segment;
      }
    }
    return null;
  };

  // FT-14: Export all SMS data
  const handleExport = async () => {
    setExporting(true);
    try {
      const excludePatterns = ['auth_', 'token', 'session', 'cache_', 'tmp_'];
      const rawKeys = {};
      const vesselData = {};

      // Iterate all localStorage keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);

        // Skip excluded keys
        if (excludePatterns.some(p => key.toLowerCase().includes(p))) continue;

        try {
          const value = localStorage.getItem(key);
          const parsed = JSON.parse(value);
          rawKeys[key] = parsed;

          // Group by vessel
          const vesselId = extractVesselId(key);
          if (vesselId) {
            if (!vesselData[vesselId]) {
              vesselData[vesselId] = { modules: {}, settings: {} };
            }
            const module = extractModule(key);
            if (module === 'vesselSettings') {
              vesselData[vesselId].settings = parsed;
            } else {
              vesselData[vesselId].modules[module] = parsed;
            }
          }
        } catch (e) {
          // Store as raw string if not JSON
          rawKeys[key] = localStorage.getItem(key);
        }
      }

      const exportData = {
        exportVersion: 1,
        exportDate: new Date().toISOString(),
        appVersion: 'sms-pro--0000131',
        organization: { id: org?.id, name: org?.name },
        vessels: Object.entries(vesselData).map(([vesselId, data]) => ({
          vesselId,
          vesselName: data.settings?.vesselName || 'Unknown',
          modules: data.modules,
          settings: data.settings
        })),
        rawKeys
      };

      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const orgName = (org?.name || 'fleetskipper').replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const date = new Date().toISOString().split('T')[0];
      a.href = url;
      a.download = `fleetskipper-backup-${orgName}-${date}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const moduleCount = Object.keys(rawKeys).length;
      showAlert(`Export complete! Downloaded ${moduleCount} records.`);
    } catch (err) {
      showAlert(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  // FT-14: Import/restore from backup
  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);

      // Validate format
      if (!data.exportVersion || !data.rawKeys) {
        throw new Error('Invalid backup file format');
      }

      const recordCount = Object.keys(data.rawKeys).length;
      const exportDate = new Date(data.exportDate).toLocaleDateString();

      const confirmed = await showConfirm(
        `Restore backup from ${exportDate}?\n\nThis will merge ${recordCount} records from the backup. Existing data will be preserved, only adding or updating from the backup.`
      );

      if (!confirmed) {
        setImporting(false);
        return;
      }

      // Restore each key
      let restored = 0;
      for (const [key, value] of Object.entries(data.rawKeys)) {
        try {
          const valueStr = typeof value === 'string' ? value : JSON.stringify(value);
          localStorage.setItem(key, valueStr);

          // Dispatch event so sync picks up restored data
          window.dispatchEvent(new CustomEvent('sr:storage:set', {
            detail: { key, value, timestamp: Date.now() }
          }));
          restored++;
        } catch (e) {
          console.warn(`Failed to restore key: ${key}`, e);
        }
      }

      showAlert(`Restored ${restored} records from backup. Refresh the page to see changes.`);
    } catch (err) {
      showAlert(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* FT-99: Personal Details - Name Edit */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Personal Details</h3>
          <p className="mt-1 text-sm text-gray-500">
            Your display name appears in sign-offs and audit trails
          </p>
        </div>

        <div className="p-6">
          <div className="flex items-start space-x-3">
            <User className="h-8 w-8 text-cyan-600 mt-1" />
            <div className="flex-1">
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  id="fullName"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Enter your full name"
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
                />
                <button
                  onClick={handleSaveName}
                  disabled={savingName || editName.trim() === currentUser?.name}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingName ? 'Saving...' : 'Save'}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                This name will appear as "{editName.trim() || 'Your Name'}" in all sign-offs
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* FT-98: Simplified Account Overview - removed license/plan UI */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Account Overview</h3>
          <p className="mt-1 text-sm text-gray-500">
            Your organisation and account details
          </p>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Organisation Info */}
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-8 w-8 text-cyan-600" />
            <div>
              <h4 className="text-lg font-medium text-gray-900">
                {org?.name || 'FleetSkipper Account'}
              </h4>
              <p className="text-sm text-gray-500">
                FleetSkipper
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change */}
      <PasswordChangeCard />

      {/* FT-98: Simplified Quick Actions - removed Users/Billing buttons, kept Data Backup */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {/* FT-14: Data Export/Backup */}
        <div className="bg-white shadow rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Download className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-4 flex-1">
                <h4 className="text-lg font-medium text-gray-900">Data Backup</h4>
                <p className="text-sm text-gray-500">
                  Download or restore your SMS data
                </p>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <button
                onClick={handleExport}
                disabled={exporting}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? 'Exporting...' : 'Export Data'}
              </button>
              <label className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                {importing ? 'Importing...' : 'Restore Backup'}
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  disabled={importing}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Phase 6: Admin Access Log - Customer Transparency */}
      {/* FT-104: Only show to admin roles (super_admin, fleet) */}
      {isAdmin && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-5 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Admin Access Log</h3>
            <p className="mt-1 text-sm text-gray-500">
              View all admin access to your organization data
            </p>
          </div>
          <div className="p-6">
            <AccessLogTab />
          </div>
        </div>
      )}

      {/* FT-379: Privacy & Diagnostics - static transparency note */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <ShieldCheck className="h-6 w-6 text-cyan-600" />
            <h3 className="text-lg font-medium text-gray-900">Privacy &amp; Diagnostics</h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Your data — crew, drills, maintenance, logs and so on — stays within your
            organisation's account and isn't shared with other organisations.
          </p>
          <p className="text-sm text-gray-600">
            To keep the app reliable, we collect anonymous technical diagnostics (for example,
            when a record doesn't save correctly). These contain no personal information and no
            record content — only anonymised identifiers and timestamps — and are used solely to
            find and fix data problems.
          </p>
        </div>
      </div>

      {/* FT-459: Custom dialog hosts — always mounted in the main return so
          showConfirm/showAlert render immediately (e.g. the backup-restore
          confirmation). Without these the awaited confirm hangs and the
          restore never fires. */}
      <ConfirmDialog />
      <AlertDialog />
    </div>
  );
};

export default AccountPage;